import { put, select, takeLatest, call, all } from 'redux-saga/effects';
import { formatUnits, Provider, Signer } from 'ethers';
import { Errors } from '@blink/components/src/constants/errors';
import Logger from '@blink/components/src/utils/logger';

import { getWalletInfoSaga } from './common';

import { Store } from '@/shared/store';
import {
    agreementErrorAction,
    clearErrorsAction,
    tokensErrorAction,
    vaultErrorAction,
} from '@/shared/store/errors/actions';
import {
    setTokensAction,
    getTokensAction,
    setTokensLoadingFalseAction,
    setVaultTokensAction,
} from '@/shared/store/tokens/actions';
import { GET_LINKED_POOLS, GET_VAULTS } from '@/shared/store/vaults/constants';
import {
    setVaultPools,
    setVaultsAction,
    setVaultsLoadingFalseAction,
} from '@/shared/store/vaults/actions';
import { mapAgreementData, mapVaultData } from '@/shared/utils/helpers';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import { getVaultDetails, getVaults, Vault, VaultDetails } from '@/shared/api/vault';
import { Token, getTokens, Metadata, State, Agreement } from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { VAULT_ERROR, TOKENS_ERROR, AGREEMENTS_ERROR } from '@/shared/store/errors/constants';

// These addresses are ETH, USD and BTC which are not actual tokens but just a conventions
const excludedAddress = [
    '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    '0x0000000000000000000000000000000000000348',
    '0xbBbBBBBbbBBBbbbBbbBbbbbBBbBbbbbBbBbbBBbB',
];

/**
 *  Loading tokens and vaults
 */
export function* fetchTokensAndAgreements(withoutCache?: boolean) {
    try {
        getTokensAction();

        const [tokens, vaults]: [Token[], Vault[]] = yield all([
            call(getTokens),
            call(getVaults, withoutCache),
        ]);

        if (!vaults) {
            yield put(setVaultsLoadingFalseAction());
            return null;
        }

        if (!tokens) {
            yield put(tokensErrorAction({ message: Errors.TOKENS }));
            yield put(setTokensLoadingFalseAction());
            return null;
        } else {
            yield put(setTokensAction(tokens));
        }

        return { tokens, vaults };
    } catch (e: any) {
        yield put(
            vaultErrorAction({
                message:
                    e?.status === 403 || e?.status === 431
                        ? Errors.PERMISSION_DENIED + ' to get vaults'
                        : Errors.VAULTS + ' ' + e.message,
            }),
        );
        return null;
    }
}

/**
 *  Filtering of tokens and agreements
 */
export function* filterTokensByVaults(tokens: Token[], vaults: Vault[], isNoWallet?: boolean) {
    const filteredTokens = tokens.filter(
        (token) => !excludedAddress.includes((token.address || '').trim()),
    );

    const tokensByVaults = vaults
        .map((vault) => {
            const token = filteredTokens.find(
                (filteredToken) => filteredToken.id === vault.leverageTokenId,
            );

            if (!token) {
                return null;
            }

            token!.agreementAddress = vault.address;
            vault.noWalletInfo = isNoWallet || false;
            return token as Token;
        })
        .filter(Boolean) as Token[];

    if (!tokensByVaults) {
        yield put(tokensErrorAction({ message: Errors.NO_TOKENS_FOR_AGREEMENT }));
        return;
    }

    return {
        filteredTokens,
        tokensByVaults,
    };
}

/**
 *  Load token balances and prices
 */
export function* fetchTokenBalances(
    tokensByVaults: Token[],
    walletAddress: string,
    signer: Signer,
): Generator<any, Token[], Token[]> {
    const tokenBalances = yield all(
        tokensByVaults.map((token) =>
            call(function* () {
                try {
                    const tokenContract = getSafeContract(token.address as string, IERCAbi, signer);

                    const userBalance = yield tokenContract.balanceOf(walletAddress);

                    token.userBalance = formatUnits(userBalance as string, token.decimals);
                } catch (e: any) {
                    Logger.error('Error in vaults::tokenContract:balanceOf:', { error: e });

                    if (e?.code === 'BAD_DATA' || e?.code === 'CONTRACT_METHOD_ERROR') {
                        return { ...token };
                    }

                    yield put(
                        tokensErrorAction({
                            message:
                                e.message || Errors.TOKEN_CONTRACT_NOT_AVAILABLE + ' ' + token.name,
                        }),
                    );
                    return { ...token };
                }
                return token;
            }),
        ),
    );
    yield put(setVaultTokensAction(tokenBalances as Token[]));
    return tokenBalances as Token[];
}

/**
 * Download agreement details
 */
export function* fetchVaultsDetails(resultVaults: Vault[], signer: Signer | Provider) {
    const { walletAddress }: { walletAddress: string } = yield select((state: Store) => ({
        walletAddress: state.wallets.active.address,
    }));
    yield all(
        resultVaults.map((vault) =>
            call(function* () {
                const vaultAddress = vault.address || '';

                try {
                    const vaultContract = getSafeContract(vaultAddress, AgreementABI, signer);

                    const [metadata, state]: [Metadata, State] = yield vaultContract.info();

                    vault.collaterals = Array.from(metadata.collaterals);
                    vault.investors = Array.from(metadata.liquidityProviders);
                    vault.curators = Array.from(metadata.assetManagers);
                    vault.tokens = Array.from(state.tokens);

                    const leverageContract = getSafeContract(metadata.leverage, IERCAbi, signer);

                    const [
                        totalPosition,
                        poolBalance,
                        interestAccrued,
                        totalBorrowed,
                        totalDeposited,
                        vaultDetails,
                        suspended,
                    ]: [bigint, bigint, bigint, bigint, bigint, VaultDetails, boolean] = yield all([
                        vaultContract.balanceOf(walletAddress),
                        leverageContract.balanceOf(vaultAddress),
                        vaultContract.pendingRewards(walletAddress),
                        vaultContract.totalBorrowed(),
                        vaultContract.totalDeposited(),
                        call(getVaultDetails, vaultAddress),
                        vaultContract.suspended(),
                    ]);

                    vault.suspended = suspended;

                    vault.totalPosition = formatUnits(totalPosition, vault.leverage.decimals);

                    vault.interestAccrued = formatUnits(interestAccrued, vault.leverage.decimals);
                    vault.totalBorrowed = formatUnits(totalBorrowed, vault.leverage.decimals);
                    vault.totalDeposited = formatUnits(totalDeposited, vault.leverage.decimals);

                    if (totalDeposited === 0n) {
                        vault.poolUtilization = '0';
                    } else {
                        vault.poolUtilization = Math.min(
                            (Number(vault.totalBorrowed) / Number(vault.totalDeposited)) * 100,
                            100,
                        ).toFixed(2);
                    }

                    vault.description = vaultDetails.description;

                    vault.poolBalance = formatUnits(poolBalance, vault.leverage.decimals);

                    vault.totalDepositThreshold = formatUnits(
                        metadata.totalDepositThreshold,
                        vault.leverage.decimals,
                    );

                    vault.withdrawable = Math.min(
                        Number(vault.totalPosition!),
                        Number(vault.poolBalance!),
                    ).toFixed(8);

                    vault.interestClaimable = Math.min(
                        Number(vault.interestAccrued!),
                        Number(vault.poolBalance!),
                    ).toFixed(8);
                } catch (e: any) {
                    vault.errorContract = true;
                    Logger.error('Error in vaults::vaultContract or leverageContract:', {
                        error: e,
                    });

                    if (e?.code === 'BAD_DATA' || e?.code === 'CONTRACT_METHOD_ERROR') {
                        return { ...vault };
                    }

                    yield put(
                        vaultErrorAction({
                            message:
                                e.message ||
                                Errors.VAULTS_CONTRACT_NOT_AVAILABLE + ' ' + vault.title,
                        }),
                    );
                    return { ...vault };
                }
            }),
        ),
    );

    yield put(setVaultsAction(resultVaults as Vault[]));
}

function* getVaultsSaga(action: {
    type: string;
    payload: {
        withoutCache?: boolean;
    };
}): Generator<any, void, any> {
    try {
        const { withoutCache } = action.payload;

        yield put(clearErrorsAction({ errorType: VAULT_ERROR }));
        yield put(clearErrorsAction({ errorType: TOKENS_ERROR }));

        const walletData = yield call(getWalletInfoSaga, setVaultsLoadingFalseAction);
        if (!walletData) {
            const isNoWallet = true;
            yield put(setVaultsLoadingFalseAction());

            const data = yield call(fetchTokensAndAgreements, withoutCache);
            if (!data) return;
            const { tokens, vaults } = data;

            const filteredData = yield call(filterTokensByVaults, tokens, vaults, isNoWallet);
            if (!filteredData) return;
            const { tokensByVaults } = filteredData;

            const resultVaults = mapVaultData(vaults, tokensByVaults);

            yield put(setVaultsAction(resultVaults as Vault[]));

            return;
        }
        const { walletAddress, signer } = walletData;

        if (!walletAddress) return;

        const data = yield call(fetchTokensAndAgreements, withoutCache);
        if (!data) return;
        const { tokens, vaults } = data;

        const filteredData = yield call(filterTokensByVaults, tokens, vaults);
        if (!filteredData) return;
        const { tokensByVaults } = filteredData;

        yield call(fetchTokenBalances, tokensByVaults, walletAddress, signer);

        const resultVaults = mapVaultData(vaults, tokensByVaults);

        yield call(fetchVaultsDetails, resultVaults, signer);
    } catch (e: any) {
        yield put(vaultErrorAction({ message: Errors.VAULTS + ' ' + e.message }));
        Logger.error('Error in vaults:getVaultsSaga:', { error: e });
    }
}

/**
 *  Load vaults token balances and prices
 */
export function* fetchVaultsTokenBalances(
    uniqueLeverageTokens: Token[],
    marginAccountAddress: string,
    signer: Signer | Provider,
): Generator<any> {
    type BalancesMap = Map<string, bigint>;

    const results = (yield all(
        uniqueLeverageTokens.map((token) =>
            call(function* (): Generator<any, [string, bigint] | undefined, any> {
                try {
                    const tokenContract = getSafeContract(token.address as string, IERCAbi, signer);

                    const balance = yield tokenContract.balanceOf(marginAccountAddress);

                    return [token.address, BigInt(balance)] as [string, bigint];
                } catch (e: any) {
                    Logger.error('Error in vaults::tokenContract:balanceOf:', {
                        error: e,
                    });

                    yield put(
                        tokensErrorAction({
                            message:
                                e.message || Errors.TOKEN_CONTRACT_NOT_AVAILABLE + ' ' + token.name,
                        }),
                    );
                }
            }),
        ),
    )) as Array<[string, bigint] | undefined>;

    const filteredResults: [string, bigint][] = results.filter((item): item is [string, bigint] =>
        Array.isArray(item),
    );

    const tokenBalances: BalancesMap = new Map(filteredResults);

    return { balances: tokenBalances };
}

/**
 *  Get linked pools
 */
export function* fetchResultLinkedPools(
    linkedPools: Agreement[],
    balances: any,
    marginAccountAddress: string,
    signer: Signer | Provider,
): Generator<any> {
    const resultLinkedPools = yield all(
        linkedPools.map((agreement) =>
            call(function* () {
                const agreementAddress = agreement.address || '';

                try {
                    const agreementContract = getSafeContract(
                        agreementAddress,
                        AgreementABI,
                        signer,
                    );

                    const [metadata, state]: [Metadata, State] = yield agreementContract.info();

                    agreement.collaterals = Array.from(metadata.collaterals);
                    agreement.lenders = Array.from(metadata.liquidityProviders);
                    agreement.borrowers = Array.from(metadata.assetManagers);
                    agreement.tokens = Array.from(state.tokens);
                    agreement.userValue = formatUnits(
                        balances.get(agreement.leverage.address || '') || 0n,
                        agreement.leverage.decimals,
                    );

                    const leverageContract = getSafeContract(metadata.leverage, IERCAbi, signer);

                    const [
                        totalPosition,
                        poolBalance,
                        interestAccrued,
                        totalBorrowed,
                        totalDeposited,
                    ]: [bigint, bigint, bigint, bigint, bigint] = yield all([
                        agreementContract.balanceOf(marginAccountAddress),
                        leverageContract.balanceOf(agreementAddress),
                        agreementContract.pendingRewards(marginAccountAddress),
                        agreementContract.totalBorrowed(),
                        agreementContract.totalDeposited(),
                    ]);

                    agreement.totalPosition = formatUnits(
                        totalPosition,
                        agreement.leverage.decimals,
                    );

                    agreement.interestAccrued = formatUnits(
                        interestAccrued,
                        agreement.leverage.decimals,
                    );
                    agreement.totalBorrowed = formatUnits(
                        totalBorrowed,
                        agreement.leverage.decimals,
                    );
                    agreement.totalDeposited = formatUnits(
                        totalDeposited,
                        agreement.leverage.decimals,
                    );

                    if (totalDeposited === 0n) {
                        agreement.poolUtilization = '0';
                    } else {
                        agreement.poolUtilization = Math.min(
                            (Number(agreement.totalBorrowed) / Number(agreement.totalDeposited)) *
                                100,
                            100,
                        ).toFixed(2);
                    }

                    agreement.poolBalance = formatUnits(poolBalance, agreement.leverage.decimals);

                    agreement.totalDepositThreshold = formatUnits(
                        metadata.totalDepositThreshold,
                        agreement.leverage.decimals,
                    );

                    agreement.withdrawable = Math.min(
                        Number(agreement.totalPosition!),
                        Number(agreement.poolBalance!),
                    ).toFixed(8);

                    agreement.interestClaimable = Math.min(
                        Number(agreement.interestAccrued!),
                        Number(agreement.poolBalance!),
                    ).toFixed(8);

                    return agreement;
                } catch (e: any) {
                    Logger.error('Error in vaults::agreementContract or leverageContract:', {
                        error: e,
                    });

                    if (e?.code === 'BAD_DATA' || e?.code === 'CONTRACT_METHOD_ERROR') {
                        return { ...agreement };
                    }

                    yield put(
                        agreementErrorAction({
                            message:
                                e.message ||
                                Errors.AGREEMENT_CONTRACT_NOT_AVAILABLE + ' ' + agreement.title,
                        }),
                    );
                    return { ...agreement };
                }
            }),
        ),
    );
    return { resultLinkedPools };
}

function* getLinkedPoolsSaga(action: {
    type: string;
    payload: {
        vaultAddress: string;
        marginAccountAddress: string;
        withoutCache?: boolean;
    };
}): Generator<any, void, any> {
    try {
        yield put(clearErrorsAction({ errorType: VAULT_ERROR }));
        yield put(clearErrorsAction({ errorType: AGREEMENTS_ERROR }));
        yield put(clearErrorsAction({ errorType: TOKENS_ERROR }));

        const { vaultAddress, marginAccountAddress, withoutCache } = action.payload;

        const { tokens }: { tokens: Token[] } = yield select((state: Store) => ({
            tokens: state.tokens.tokens,
        }));

        const walletData = yield call(getWalletInfoSaga, setVaultsLoadingFalseAction);
        if (!walletData) return;
        const { signer } = walletData;

        const vaultDetails = yield call(getVaultDetails, vaultAddress, withoutCache);

        const linkedPools: Agreement[] = mapAgreementData(vaultDetails.linkedPools, tokens);

        const uniqueLeverageTokens = Array.from(
            new Map(linkedPools.map((pool) => [pool.address, pool.leverage])).values(),
        );

        const balancesData = yield call(
            fetchVaultsTokenBalances,
            uniqueLeverageTokens,
            marginAccountAddress,
            signer,
        );
        if (!balancesData) return;
        const { balances } = balancesData;

        const linkedPoolsData = yield call(
            fetchResultLinkedPools,
            linkedPools,
            balances,
            marginAccountAddress,
            signer,
        );
        if (!linkedPoolsData) return;
        const { resultLinkedPools } = linkedPoolsData;

        yield put(
            setVaultPools({
                vaultAddress,
                linkedPools: resultLinkedPools,
            }),
        );
    } catch (err) {
        yield put(vaultErrorAction({ message: Errors.VAULTS }));
        Logger.error('Error in vaults:getMarginAccountsSaga:', { error: err });
    }
}

export function* vaultsWatcher() {
    yield takeLatest(GET_VAULTS, getVaultsSaga);
    yield takeLatest(GET_LINKED_POOLS, getLinkedPoolsSaga);
}
